import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { useSEO } from '@/hooks/useSEO';
import { categories, categoryMap } from '@/mocks/categories';
import { getComicsByCategory } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug = 'rexiao' } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  const categoryName = categoryMap[slug] || '热门推荐';
  const comics = getComicsByCategory(slug);

  useSEO({
    title: `${categoryName} - 韩国免费漫画分类_漫画之家`,
    description: `漫画之家提供海量${categoryName}类韩国免费漫画，下拉式流畅阅读体验，第一时间追更热门韩漫。全部免费，分类齐全，尽在漫画之家。`,
    keywords: `${categoryName},韩国漫画,免费韩漫,漫画之家`,
    canonicalUrl: `https://www.manhuazhijia.com/category/${slug}`,
    schemas: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: `${categoryName} - 韩国免费漫画`,
        description: `漫画之家${categoryName}分类，提供海量韩国免费漫画在线免费阅读。`,
        url: `https://www.manhuazhijia.com/category/${slug}`,
        isPartOf: {
          '@type': 'WebSite',
          name: '漫画之家',
          url: 'https://www.manhuazhijia.com',
        },
      },
    ],
  });

  return (
    <div className="min-h-screen bg-background-50">
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="bg-white border-b border-background-200/70">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: t('breadcrumb.home'), href: '/' },
                { label: t('breadcrumb.category') },
                { label: categoryName },
              ]}
            />
          </div>
        </div>

        <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900">
              {categoryName}
            </h1>
            <p className="text-sm text-foreground-500 mt-2">{t('category.desc')}</p>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-8 justify-center">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <p className="text-sm text-foreground-500 mb-6 text-center">
            {t('category.resultCount', { count: comics.length })}
          </p>

          {comics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
              {comics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">{t('category.empty')}</p>
              <Link
                to="/category/rexiao"
                className="inline-block mt-4 text-sm text-primary-500 hover:text-primary-600 font-medium"
              >
                去看看热门推荐 →
              </Link>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}